import type { Dayjs } from "dayjs";

import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
// oxlint-disable-next-line import/no-duplicates
import relativeTime from "dayjs/plugin/relativeTime";
import updateLocale from "dayjs/plugin/updateLocale";
import weekOfYear from "dayjs/plugin/weekOfYear";

import type { SolvariEnv } from "./solvariEnv.ts";

import { isClient } from "./isClient.ts";
import { singleton } from "./memoize.ts";
import { getEnv } from "./solvariEnv.ts";

import "dayjs/locale/da";
import "dayjs/locale/fr";
import "dayjs/locale/nl";
import "dayjs/locale/sv";
// The default import above is elided from the emitted .d.ts (it's only used as
// a value), so this side-effect import preserves the relativeTime type
// augmentation (.fromNow()) for consumers of @solvari/utils.
import "dayjs/plugin/relativeTime";

type SDayjs = Dayjs & {
  week(): number;
  week(value: number): SDayjs;
};

type LocaleCode = SolvariEnv["config"]["localeCode"];

type DayjsFn = (date?: dayjs.ConfigType, locale?: LocaleCode) => SDayjs;

const dayjsLocales = {
  "be-FR": "fr",
  "be-NL": "nl",
  "dk-DA": "da",
  "fr-FR": "fr",
  "nl-NL": "nl",
  "se-SV": "sv",
} as const satisfies Record<LocaleCode, string>;

// Process-wide on the server, so request-independent setup only — the locale is set per instance.
const dayjsInstance = singleton(() => {
  dayjs.extend(duration);
  dayjs.extend(relativeTime);
  dayjs.extend(updateLocale);
  dayjs.extend(LocalizedFormat);
  dayjs.extend(weekOfYear);

  dayjs.updateLocale("nl", {
    calendar: {
      lastDay: "[Gisteren]",
      sameDay: "[Vandaag]",
      nextDay: "[Morgen]",
      lastWeek: "dd D MMMM",
      nextWeek: "dd D MMMM",
      sameElse: "dd D MMMM",
    },
  });

  dayjs.updateLocale("fr", {
    calendar: {
      lastDay: "[Hier]",
      sameDay: "[Aujourd'hui]",
      nextDay: "[Demain]",
      lastWeek: "dd D MMMM",
      nextWeek: "dd D MMMM",
      sameElse: "dd D MMMM",
    },
  });

  // Direct `dayjs` package imports still rely on the global locale; a browser page has one.
  if (isClient()) {
    dayjs.locale(dayjsLocales[getEnv().config.localeCode]);
  }

  return dayjs;
});

/**
 * Pass `locale` (captured at setup) when calling outside the request context, e.g. from
 * a head getter — without it the locale comes from `getEnv()`, which throws there on the server.
 */
const sDayjs: DayjsFn = (date, locale) => {
  return dayjsInstance()(date).locale(
    dayjsLocales[locale ?? getEnv().config.localeCode],
  );
};

function toDate(value: Date | number | string, locale?: LocaleCode): string {
  if (!value) {
    return value.toString();
  }
  return sDayjs(value.toString(), locale).format("DD-MM-YYYY");
}

function toDateMedium(
  value: Date | number | string,
  locale?: LocaleCode,
): string {
  if (!value) {
    return value.toString();
  }
  return sDayjs(value.toString(), locale).format("D MMM YYYY");
}

function toDateTime(
  value: Date | number | string,
  locale?: LocaleCode,
): string {
  if (!value) {
    return value.toString();
  }
  return sDayjs(value.toString(), locale).format("DD-MM-YYYY HH:mm:ss");
}

function toCountdown(value: number): string {
  return dayjsInstance().duration(value, "seconds").format("mm:ss");
}

export type { SDayjs as Dayjs };
export { sDayjs as dayjs, toCountdown, toDate, toDateMedium, toDateTime };
